import { useState, useEffect } from 'react';
import api, { todayStr, formatDate, formatDateShort } from '../lib/api';
import { toast } from 'sonner';
import { Clock, Phone, Mail, Users, ArrowRight, Trash2, RefreshCw, X, MessageCircle, ChevronDown, Plus } from 'lucide-react';
import { MiniFloorPlanPicker } from '../components/ReservationDialogs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const STATUS_LABELS = {
  new: 'Neu',
  waiting: 'Wartet',
  offered: 'Angebot gesendet',
  expired: 'Abgelaufen',
  confirmed: 'Bestätigt',
  declined: 'Abgelehnt',
  converted: 'In Reservierung umgewandelt',
};
const STATUS_COLORS = {
  new: '#3B82F6', waiting: '#EAB308', offered: '#F97316',
  expired: '#9CA3AF', confirmed: '#22C55E', declined: '#9CA3AF', converted: '#22C55E',
};
// Herkunft der Warteliste/Warteschlange
const SOURCE_META = {
  online: { label: 'Online', color: '#3B82F6' },
  walk_in: { label: 'Vor Ort', color: '#8B5CF6' },
  phone: { label: 'Telefon', color: '#0EA5E9' },
};

export default function AdminWaitlist() {
  const [list, setList] = useState([]);
  const [areas, setAreas] = useState([]);
  const [tables, setTables] = useState([]);
  const [allCombinations, setAllCombinations] = useState([]);
  const [date, setDate] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  // Convert dialog state
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertWl, setConvertWl] = useState(null);
  const [convertStartTime, setConvertStartTime] = useState('');
  const [convertTableIds, setConvertTableIds] = useState([]);
  const [convertDayResvs, setConvertDayResvs] = useState([]);
  const [convertActiveArea, setConvertActiveArea] = useState(null);
  // Manuell hinzufügen (Vor Ort / Telefon)
  const [addOpen, setAddOpen] = useState(false);
  const emptyAddForm = { customer_name: '', phone: '', persons: 2, source: 'walk_in', preferred_time: '', note: '', date: todayStr() };
  const [addForm, setAddForm] = useState(emptyAddForm);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (status) params.append('status', status);
    try {
      const [r, a, t, c] = await Promise.all([
        api.get(`/waitlist?${params.toString()}`),
        api.get('/areas'),
        api.get('/tables'),
        api.get('/table-combinations/all').catch(() => ({ data: [] })),
      ]);
      setList(r.data);
      setAreas(a.data);
      setTables(t.data || []);
      setAllCombinations(c.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, status]);

  const openConvert = async (wl) => {
    setConvertWl(wl);
    const nowHM = `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`;
    setConvertStartTime(wl.matched_reservation_time || wl.preferred_time || nowHM);
    setConvertTableIds([]);
    setConvertActiveArea(wl.preferred_area_id || null);
    setConvertOpen(true);
    try {
      const r = await api.get(`/reservations?date=${wl.date}`);
      setConvertDayResvs(r.data || []);
    } catch {
      setConvertDayResvs([]);
    }
  };

  const submitAdd = async () => {
    if (!addForm.customer_name.trim()) { toast.error('Bitte einen Namen eingeben'); return; }
    try {
      await api.post('/waitlist/manual', {
        customer_name: addForm.customer_name.trim(),
        phone: addForm.phone || '',
        persons: parseInt(addForm.persons) || 1,
        source: addForm.source,
        date: addForm.date || todayStr(),
        preferred_time: addForm.preferred_time || '',
        note: addForm.note || '',
      });
      toast.success('Zur Warteliste hinzugefügt');
      setAddOpen(false);
      setAddForm(emptyAddForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const submitConvert = async () => {
    if (!convertWl) return;
    try {
      await api.post(`/waitlist/${convertWl.id}/convert`, {
        table_ids: convertTableIds,
        start_time: convertStartTime || null,
      });
      toast.success('Reservierung erstellt');
      setConvertOpen(false);
      setConvertWl(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Umwandeln');
    }
  };
  const del = async (wl) => {
    if (!window.confirm(`Wartelisten-Eintrag von ${wl.customer_name} löschen?`)) return;
    await api.delete(`/waitlist/${wl.id}`);
    toast.success('Gelöscht');
    load();
  };
  const updateStatus = async (wl, newStatus) => {
    await api.put(`/waitlist/${wl.id}`, { status: newStatus });
    toast.success(STATUS_LABELS[newStatus]);
    load();
  };
  const declineWl = async (wl) => {
    const hasEmail = !!wl.email;
    const msg = hasEmail
      ? `Wartelisten-Anfrage von ${wl.customer_name} ablehnen? Der Gast erhält automatisch eine Absage-E-Mail.`
      : `Wartelisten-Anfrage von ${wl.customer_name} ablehnen? (Keine E-Mail hinterlegt – es wird keine Nachricht versendet.)`;
    if (!window.confirm(msg)) return;
    await api.put(`/waitlist/${wl.id}`, { status: 'declined' });
    toast.success(hasEmail ? 'Abgelehnt – Absage-E-Mail gesendet' : 'Abgelehnt');
    load();
  };
  const updatePriority = async (wl, delta) => {
    await api.put(`/waitlist/${wl.id}`, { priority: (wl.priority || 100) + delta });
    load();
  };

  const WA_TEMPLATES = [
    {
      key: 'free',
      label: 'Tisch frei!',
      build: (wl) => `Hallo ${wl.customer_name},\n\nIhr Tisch im La Contadina ist jetzt frei! 🎉\nKommen Sie gerne vorbei – wir freuen uns auf Sie.`,
    },
    {
      key: 'soon',
      label: 'Bald frei',
      build: (wl) => `Hallo ${wl.customer_name},\n\nIhr Tisch im La Contadina wird voraussichtlich in Kürze frei. Bitte halten Sie sich bereit – wir melden uns gleich wieder bei Ihnen.`,
    },
    {
      key: 'confirm',
      label: 'Warteliste bestätigen',
      build: (wl) => `Hallo ${wl.customer_name},\n\nwir haben Ihre Anfrage im La Contadina auf die Warteliste gesetzt:\n📅 ${formatDateShort(wl.date)}\n🕖 ${wl.preferred_time} Uhr\n👥 ${wl.persons} Personen\n\nSobald ein passender Tisch frei wird, melden wir uns umgehend bei Ihnen.`,
    },
    {
      key: 'full',
      label: 'Leider ausgebucht',
      build: (wl) => `Hallo ${wl.customer_name},\n\nleider sind wir im La Contadina für ${formatDateShort(wl.date)} um ${wl.preferred_time} Uhr ausgebucht. Gerne schlagen wir Ihnen eine andere Uhrzeit oder einen anderen Tag vor – melden Sie sich einfach bei uns.`,
    },
  ];

  const sendWhatsApp = (wl, tpl) => {
    const digits = (wl.phone || '').replace(/[^\d]/g, '');
    if (!digits) {
      toast.info('Keine Telefonnummer hinterlegt – bitte zuerst eine Telefonnummer eintragen.');
      return;
    }
    const num = digits.replace(/^00/, '').replace(/^0/, '49');
    const msg = tpl.build(wl);
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl" data-testid="admin-waitlist-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Wartelisten-Anfragen</p>
          <h1 className="font-display text-3xl text-[#1C1C19]">Warteliste</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setAddForm(emptyAddForm); setAddOpen(true); }} data-testid="wl-add-manual" className="px-3 py-2 bg-[#1C1C19] text-white rounded text-sm hover:bg-[#3A3A35] flex items-center gap-1">
            <Plus className="w-4 h-4" /> Manuell hinzufügen
          </button>
          <button onClick={load} data-testid="wl-refresh" className="px-3 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] flex items-center gap-1">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </button>
        </div>
      </header>

      <div className="bg-white border border-[#E5E5E0] rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Datum</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="wl-filter-date" className="px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} data-testid="wl-filter-status" className="px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
            <option value="">Alle</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {(date || status) && (
          <button onClick={() => { setDate(''); setStatus(''); }} data-testid="wl-clear-filters" className="text-xs text-[#5C5C59] hover:text-[#1C1C19] underline">Filter zurücksetzen</button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
        {list.length === 0 ? (
          <div className="p-12 text-center text-[#5C5C59]" data-testid="wl-empty">Keine Wartelisten-Einträge.</div>
        ) : list.map(wl => {
          const area = areas.find(a => a.id === wl.preferred_area_id);
          return (
            <div key={wl.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`wl-row-${wl.id}`}>
              <div className="flex items-center gap-3 md:w-32">
                <span className="w-1 h-10 rounded" style={{ background: STATUS_COLORS[wl.status] }}></span>
                <div>
                  <div className="font-mono text-base text-[#1C1C19]">{wl.preferred_time || '—'}</div>
                  <div className="text-xs text-[#5C5C59]">{formatDateShort(wl.date)}</div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium text-[#1C1C19]">{wl.customer_name}</h3>
                  {(() => {
                    const m = SOURCE_META[wl.source] || SOURCE_META.online;
                    return (
                      <span data-testid={`wl-source-${wl.id}`} className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: m.color + '22', color: m.color }}>
                        {m.label}
                      </span>
                    );
                  })()}
                  <span className="text-xs px-2 py-0.5 rounded font-mono uppercase tracking-wider" style={{ background: STATUS_COLORS[wl.status] + '22', color: STATUS_COLORS[wl.status] }}>
                    {STATUS_LABELS[wl.status]}
                  </span>
                </div>
                <div className="text-xs text-[#5C5C59] mt-1 flex flex-wrap items-center gap-3">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {wl.persons}</span>
                  {wl.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {wl.phone}</span>}
                  {wl.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {wl.email}</span>}
                  {area && <span className="text-xs px-1.5 py-0.5 rounded border" style={{ borderColor: area.color, color: area.color }}>{area.name}</span>}
                  {wl.accepts_alternative_times && <span className="text-xs italic">Akzeptiert Alternativ-Zeit</span>}
                  <span className="font-mono">Prio: {wl.priority}</span>
                </div>
                {wl.note && <div className="text-xs text-[#5C5C59] italic mt-1">&ldquo;{wl.note}&rdquo;</div>}
              </div>
              <div className="flex gap-1 flex-wrap justify-end">
                <button onClick={() => updatePriority(wl, 10)} data-testid={`wl-prio-up-${wl.id}`} className="text-xs px-2 py-1 border border-[#E5E5E0] rounded hover:border-[#1C1C19]">↑</button>
                <button onClick={() => updatePriority(wl, -10)} data-testid={`wl-prio-down-${wl.id}`} className="text-xs px-2 py-1 border border-[#E5E5E0] rounded hover:border-[#1C1C19]">↓</button>
                {wl.status === 'waiting' && (
                  <button onClick={() => updateStatus(wl, 'offered')} data-testid={`wl-offer-${wl.id}`} className="text-xs px-3 py-1.5 border border-[#F97316] text-[#F97316] rounded hover:bg-[#F97316] hover:text-white">Angebot</button>
                )}
                {wl.phone && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`wl-whatsapp-${wl.id}`} title="WhatsApp-Nachricht senden" className="text-xs px-3 py-1.5 bg-[#25D366] hover:bg-[#1EBE5B] text-white rounded flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" /> WhatsApp <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuLabel className="text-xs text-[#5C5C59]">Vorlage wählen</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {WA_TEMPLATES.map(tpl => (
                        <DropdownMenuItem key={tpl.key} onClick={() => sendWhatsApp(wl, tpl)} data-testid={`wl-whatsapp-${tpl.key}-${wl.id}`} className="cursor-pointer">
                          <MessageCircle className="w-3.5 h-3.5 mr-2 text-[#25D366]" /> {tpl.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {!['converted', 'declined'].includes(wl.status) && (
                  <button onClick={() => openConvert(wl)} data-testid={`wl-convert-${wl.id}`} className="text-xs px-3 py-1.5 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" /> Reservierung
                  </button>
                )}
                {!['converted', 'declined'].includes(wl.status) && (
                  <button onClick={() => declineWl(wl)} data-testid={`wl-decline-${wl.id}`} title={wl.email ? 'Ablehnen – Gast erhält eine Absage-E-Mail' : 'Ablehnen'} className="text-xs px-3 py-1.5 border border-[#EF4444] text-[#EF4444] rounded hover:bg-[#EF4444] hover:text-white flex items-center gap-1 transition">
                    <X className="w-3 h-3" /> Ablehnen
                  </button>
                )}
                <button onClick={() => del(wl)} data-testid={`wl-delete-${wl.id}`} className="p-1.5 hover:bg-[#FEE2E2] rounded" title="Eintrag löschen">
                  <Trash2 className="w-4 h-4 text-[#EF4444]" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Convert dialog with mini floor plan picker */}
      <Dialog open={convertOpen} onOpenChange={(v) => !v && setConvertOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wl-convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">In Reservierung umwandeln</DialogTitle>
          </DialogHeader>
          {convertWl && (
            <div className="space-y-3">
              <div className="bg-[#F9F9F7] border border-[#E5E5E0] rounded p-3 text-sm grid grid-cols-2 gap-2">
                <div><span className="text-[#5C5C59] text-xs uppercase tracking-wider">Gast</span><div className="font-medium">{convertWl.customer_name}</div></div>
                <div><span className="text-[#5C5C59] text-xs uppercase tracking-wider">Personen</span><div className="font-medium">{convertWl.persons}</div></div>
                <div><span className="text-[#5C5C59] text-xs uppercase tracking-wider">Datum</span><div className="font-medium">{formatDate(convertWl.date)}</div></div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Uhrzeit</Label>
                  <Input
                    type="time"
                    value={convertStartTime}
                    onChange={e => setConvertStartTime(e.target.value)}
                    data-testid="wl-convert-time"
                    className="mt-0.5"
                  />
                </div>
              </div>

              <div>
                <Label>Tisch (leer lassen für Auto-Zuweisung)</Label>
                <p className="text-xs text-[#5C5C59] mb-2">
                  Wenn kein Tisch gewählt: Bereich + Personenzahl + Priorität → kleinster passender freier Tisch, sonst Kombi.
                </p>
                <MiniFloorPlanPicker
                  tables={tables}
                  areas={areas}
                  combinations={allCombinations}
                  dayReservations={convertDayResvs}
                  startTime={convertStartTime}
                  durationMinutes={120}
                  persons={convertWl.persons}
                  pickedTableIds={convertTableIds}
                  setPickedTableIds={setConvertTableIds}
                  activeAreaId={convertActiveArea}
                  setActiveAreaId={setConvertActiveArea}
                  date={convertWl?.date}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setConvertOpen(false)} variant="outline" data-testid="wl-convert-cancel">Abbrechen</Button>
            <Button onClick={submitConvert} className="bg-[#22C55E] hover:bg-[#16A34A]" data-testid="wl-convert-submit">
              <ArrowRight className="w-4 h-4 mr-1" /> Reservierung erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manuell hinzufügen (Vor Ort / Telefon) */}
      <Dialog open={addOpen} onOpenChange={(v) => !v && setAddOpen(false)}>
        <DialogContent className="max-w-md" data-testid="wl-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Manuell zur Warteliste</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C59]">Herkunft</Label>
              <div className="flex gap-2 mt-1">
                {[{ k: 'walk_in', l: 'Vor Ort' }, { k: 'phone', l: 'Telefon' }].map(o => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setAddForm(f => ({ ...f, source: o.k }))}
                    data-testid={`wl-add-source-${o.k}`}
                    className={`flex-1 px-3 py-1.5 rounded border text-sm transition ${addForm.source === o.k ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'bg-white text-[#1C1C19] border-[#E5E5E0] hover:border-[#1C1C19]'}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Name *</Label>
              <Input value={addForm.customer_name} onChange={e => setAddForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="wl-add-name" placeholder="Gastname" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Personen *</Label>
                <Input type="number" min="1" value={addForm.persons} onChange={e => setAddForm(f => ({ ...f, persons: e.target.value }))} data-testid="wl-add-persons" />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={addForm.phone} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} data-testid="wl-add-phone" placeholder="optional" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Datum</Label>
                <Input type="date" value={addForm.date} onChange={e => setAddForm(f => ({ ...f, date: e.target.value }))} data-testid="wl-add-date" />
              </div>
              <div>
                <Label>Wunschuhrzeit</Label>
                <Input type="time" value={addForm.preferred_time} onChange={e => setAddForm(f => ({ ...f, preferred_time: e.target.value }))} data-testid="wl-add-time" placeholder="optional" />
              </div>
            </div>
            <p className="text-xs text-[#9CA3AF]">Uhrzeit optional lassen für eine reine Vor-Ort-Warteschlange (ohne feste Zeit).</p>
            <div>
              <Label>Notiz</Label>
              <Input value={addForm.note} onChange={e => setAddForm(f => ({ ...f, note: e.target.value }))} data-testid="wl-add-note" placeholder="optional" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setAddOpen(false)} variant="outline" data-testid="wl-add-cancel">Abbrechen</Button>
            <Button onClick={submitAdd} className="bg-[#1C1C19] hover:bg-[#3A3A35]" data-testid="wl-add-submit">
              <Plus className="w-4 h-4 mr-1" /> Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
